import {
  Box,
  Button,
  Center,
  Flex,
  Icon,
  Text,
  useToast,
  Spinner,
  Image,
  Stack,
} from '@chakra-ui/react'
import { FaWhatsapp } from 'react-icons/fa'
import { useEffect, useState, useRef } from 'react'
import { useSubscription } from 'mqtt-react-hooks'
import { apiAuth } from '../../services/apiAuth'
import { useAuth } from '../../hooks/auth'

export default function WhatsappConnect() {
  const { clinicId } = useAuth()
  const toast = useToast()
  const [connected, setConnected] = useState<boolean | null>(null)
  const [connectedNumber, setConnectedNumber] = useState<string | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [disconnecting, setDisconnecting] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [qrExpiresIn, setQrExpiresIn] = useState<number | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<string>('disconnected')
  const qrTimerRef = useRef<NodeJS.Timeout | null>(null)
  const qrRefreshInterval = useRef<NodeJS.Timeout | null>(null)

  const { message: mqttMessage } = useSubscription(
    clinicId ? [`clinic/${clinicId}/whatsapp/status`] : []
  )

  useEffect(() => {
    if (!mqttMessage) return

    try {
      if (!mqttMessage?.message || typeof mqttMessage.message !== 'string') return
      const data = JSON.parse(mqttMessage.message)
      console.log('MQTT Event received:', data)

      switch (data.status) {
        case 'connected':
          setConnected(true)
          setConnectedNumber(data.user?.id || null)
          setQr(null)
          setQrExpiresIn(null)
          setConnectionStatus('connected')
          setLoading(false)
          setConnecting(false)
          
          toast({
            title: '✅ Conectado!',
            description: 'WhatsApp conectado com sucesso.',
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top',
          })
          break

        case 'syncing':
          setConnectionStatus('syncing')
          setLoading(false)
          setConnecting(false)
          toast({
            title: 'Sincronizando sessão',
            description: 'Sincronizando sessão, aguarde alguns segundos...'
          })
          break

        case 'disconnected':
          setConnected(false)
          setConnectedNumber(null)
          setQr(null)
          setQrExpiresIn(null)
          setConnectionStatus('disconnected')
          setLoading(false)
          setConnecting(false)
          setDisconnecting(false)
          
          if (data.reason !== 'manual_disconnect') {
            toast({
              title: '⚠️ Desconectado',
              description: data.reason ? `Motivo: ${data.reason}` : 'Conexão perdida',
              status: 'warning',
              duration: 4000,
              isClosable: true,
              position: 'top',
            })
          }
          break

        case 'qr_ready':
          setConnected(false)
          setQr(data.qr)
          setQrExpiresIn(data.expiresIn || 60000)
          setConnectionStatus('qr_ready')
          setLoading(false)
          setConnecting(false)
          break

        case 'connecting':
          setConnectionStatus('connecting')
          setConnecting(true)
          setLoading(false)
          break

        case 'error':
          setConnectionStatus('error')
          setLoading(false)
          setConnecting(false)
          
          toast({
            title: '❌ Erro de conexão',
            description: data.error || 'Erro desconhecido',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top',
          })
          break
      }
    } catch (error) {
      console.error('Erro ao processar mensagem MQTT:', error)
    }
  }, [mqttMessage, toast])

  async function fetchQr() {
    try {
      const { data } = await apiAuth.get('clinics/whatsapp/integration/qr/')
      if (data.qr) {
        setQr(data.qr)
        setQrExpiresIn(data.expiresIn || 60 * 1000)
      } else {
        setQr(null)
        setQrExpiresIn(null)
      }
    } catch {
      toast({
        title: 'Erro ao buscar QR Code',
        description: 'Não foi possível obter o código QR.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      })
    }
  }

  async function fetchStatus() {
    setLoading(true)
    try {
      const { data } = await apiAuth.get('clinics/whatsapp/integration/status/')
      setConnected(data.connected)
      setConnectedNumber(data.number || null)

      if (!data.connected) {
        await fetchQr()
      } else {
        setQr(null)
        setQrExpiresIn(null)
      }
    } catch {
      toast({
        title: 'Erro na conexão',
        description: 'Microserviço indisponível',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      })
    } finally {
      setLoading(false)
    }
  }

  async function disconnect() {
    if (!window.confirm('Tem certeza que deseja desconectar sua conta WhatsApp?')) return
    setDisconnecting(true)
    try {
      await apiAuth.post('clinics/whatsapp/integration/disconnect/')
      // O evento de desconexão será tratado pelo MQTT
    } catch {
      toast({
        title: 'Erro ao desconectar',
        description: 'Não foi possível desconectar a conta.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      })
      setDisconnecting(false)
    }
  }

  async function startConnection() {
    setConnecting(true)
    try {
      await apiAuth.post('clinics/whatsapp/integration/connect/')
      // O status será atualizado via MQTT, não precisamos chamar fetchStatus
    } catch {
      toast({
        title: 'Erro ao iniciar conexão',
        description: 'Não foi possível iniciar a conexão com o WhatsApp.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      })
      setConnecting(false)
    }
  }

  // Timer para countdown do QR Code
  useEffect(() => {
    if (!qr || qrExpiresIn === null) return

    if (qrTimerRef.current) clearInterval(qrTimerRef.current)

    qrTimerRef.current = setInterval(() => {
      setQrExpiresIn(prev => {
        if (prev === null) return null
        if (prev <= 1000) {
          clearInterval(qrTimerRef.current!)
          return null
        }
        return prev - 1000
      })
    }, 1000)

    return () => {
      if (qrTimerRef.current) clearInterval(qrTimerRef.current)
    }
  }, [qr, qrExpiresIn])

  // Buscar status inicial apenas uma vez
  useEffect(() => {
    fetchStatus()
  }, [])

  // Limpar intervalos ao desmontar
  useEffect(() => {
    return () => {
      if (qrTimerRef.current) clearInterval(qrTimerRef.current)
      if (qrRefreshInterval.current) clearInterval(qrRefreshInterval.current)
    }
  }, [])

  function getStatusMessage() {
    switch (connectionStatus) {
      case 'connecting':
        return '🔄 Conectando ao WhatsApp...'
      case 'syncing':
        return '⏳ Sincronizando sessão, aguarde alguns segundos...'
      case 'qr_ready':
        return '📱 QR Code pronto para escaneamento'
      case 'connected':
        return '✅ Conectado com sucesso!'
      case 'error':
        return '❌ Erro na conexão'
      case 'disconnected':
      default:
        return '📵 Desconectado'
    }
  }

  function renderContent() {
    if (loading || connectionStatus === 'syncing') return (
      <Stack spacing={4} align="center">
        <Spinner size="xl" />
        <Text fontSize="md" color="whiteAlpha.800" textAlign="center">
          {getStatusMessage()}
        </Text>
      </Stack>
    )

    if (connected) {
      return (
        <Stack spacing={4} align="center">
          <Text fontSize="lg" fontWeight="semibold" color="green.200">
            ✅ Sua conta está conectada!
          </Text>
          {connectedNumber && (
            <Text fontSize="md" color="whiteAlpha.800">
              Número: +{connectedNumber}
            </Text>
          )}
          <Text fontSize="sm" color="whiteAlpha.600">
            Status: {getStatusMessage()}
          </Text>
          <Button
            size="lg"
            colorScheme="red"
            variant="solid"
            isLoading={disconnecting}
            onClick={disconnect}
          >
            Desconectar
          </Button>
        </Stack>
      )
    }

    if (qr) {
      return (
        <Stack spacing={4} align="center">
          <Text fontSize="md" textAlign="center">
            📱 Escaneie o QR Code abaixo com o WhatsApp para conectar
          </Text>
          <Text fontSize="sm" color="whiteAlpha.600">
            Status: {getStatusMessage()}
          </Text>
          <Center>
            <Image src={qr} alt="QR Code" boxSize="256px" />
          </Center>
          {qrExpiresIn !== null && qrExpiresIn > 0 && (
            <Text fontSize="sm" color="whiteAlpha.700">
              QR Code expira em {Math.floor(qrExpiresIn / 1000)} segundos
            </Text>
          )}
          <Button 
            size="lg" 
            colorScheme="teal" 
            variant="outline" 
            onClick={fetchStatus}
            isLoading={connecting}
          >
            Verificar conexão
          </Button>
        </Stack>
      )
    }

    return (
      <Stack spacing={4} align="center">
        <Text fontSize="md" color="whiteAlpha.800" textAlign="center">
          Nenhuma conexão ativa com o WhatsApp foi encontrada.
        </Text>
        <Text fontSize="sm" color="whiteAlpha.600">
          Status: {getStatusMessage()}
        </Text>
        <Button
          size="lg"
          colorScheme="teal"
          onClick={startConnection}
          isLoading={connecting}
        >
          {connecting ? 'Conectando...' : 'Iniciar conexão com WhatsApp'}
        </Button>
      </Stack>
    )
  }

  return (
    <Flex
      direction="column"
      justify="center"
      align="center"
      bgGradient="linear(to-b, teal.500, teal.700)"
      color="white"
      p="6"
      borderRadius="lg"
      minH="400px"
    >
      <Box textAlign="center" maxW="md">
        <Icon as={FaWhatsapp} w={20} h={20} color="white" mb="6" />
        <Text fontSize="3xl" fontWeight="bold" mb="2">
          Conectar ao WhatsApp
        </Text>
        <Text fontSize="md" opacity={0.85} mb="8">
          Escaneie o QR Code com o WhatsApp ou desconecte sua conta atual.
        </Text>
        {renderContent()}
      </Box>
    </Flex>
  )
}